import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { avatarAssetPath, identityInitial } from '../../config/avatars';

/**
 * Player Identity System V1 — the one shared way to render a player avatar.
 *
 * Accepts the authoritative `avatar_key` from any payload (human preset or
 * per-session bot assignment) and validates it against the 50-key catalog
 * BEFORE any asset path is built, so a server payload or stale value can
 * never turn into an arbitrary URL. Unset, unsupported, and failed-to-load
 * keys all render the same circular display-name-initial fallback — never a
 * broken image, and never a client-picked random avatar.
 *
 * The initial derives from the canonical display name (username-first rule —
 * the backend already resolved it into `name`); it is an avatar fallback
 * only, never a replacement for rendering the full name next to it.
 */
export interface PlayerAvatarProps {
  /** Raw avatar key from any payload — validated internally. */
  avatarKey?: string | null;
  /** Canonical display name; source of the initial fallback. */
  name: string;
  /** Diameter in px (default 40). */
  size?: number;
  /**
   * Avatars are decorative by default (the name renders beside them);
   * pass false to expose an img role with the player's name.
   */
  decorative?: boolean;
  loading?: 'eager' | 'lazy';
  /** Outer style for both modes (image and initial fallback). */
  style?: CSSProperties;
  /** Extra styling for the initial-fallback circle (background/color/font). */
  initialStyle?: CSSProperties;
}

export function PlayerAvatar({
  avatarKey,
  name,
  size = 40,
  decorative = true,
  loading = 'lazy',
  style,
  initialStyle,
}: PlayerAvatarProps) {
  const src = avatarAssetPath(avatarKey);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  // A new (valid) key gets a fresh chance even after a previous load error.
  useEffect(() => { setFailedSrc(null); }, [src]);

  if (src && failedSrc !== src) {
    return (
      <img
        src={src}
        alt={decorative ? '' : name}
        aria-hidden={decorative || undefined}
        width={size}
        height={size}
        loading={loading}
        draggable={false}
        onError={() => setFailedSrc(src)}
        data-avatar-key={avatarKey ?? undefined}
        style={{
          width:        size,
          height:       size,
          borderRadius: '50%',
          objectFit:    'cover',
          display:      'inline-block',
          flexShrink:   0,
          ...style,
        }}
      />
    );
  }

  return (
    <span
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
      data-avatar-initial="true"
      style={{
        display:        'inline-flex',
        alignItems:     'center',
        justifyContent: 'center',
        width:          size,
        height:         size,
        borderRadius:   '50%',
        background:     'rgba(255,255,255,0.12)',
        border:         '1px solid rgba(255,255,255,0.25)',
        color:          '#FFFFFF',
        fontWeight:     800,
        fontSize:       Math.round(size * 0.42),
        lineHeight:     1,
        userSelect:     'none',
        flexShrink:     0,
        ...style,
        ...initialStyle,
      }}
    >
      {identityInitial(name)}
    </span>
  );
}
